import { login as apiLogin, validateSession as apiValidateSession, User } from './api';

/* eslint-disable no-undef */
// Local storage keys
const SESSION_KEY = 'user_session';
const PREFERENCES_KEY = 'user_preferences';

interface UserSession {
  token: string;
  userId: string;
  email: string;
  role: string;
  expiresAt: number;
}

interface UserPreferences {
  theme: 'light' | 'dark';
  notifications: boolean;
  language: string;
}

// Session management
let currentSession: { token: string; user: User } | null = null;

function loadSession() {
  if (typeof window === 'undefined') return null;
  const savedSession = window.localStorage.getItem(SESSION_KEY);
  return savedSession ? JSON.parse(savedSession) : null;
}

function saveSession(session: { token: string; user: User } | null) {
  if (typeof window === 'undefined') return;
  if (session) {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify({
      userId: session.user.id,
      email: session.user.email,
      role: session.user.role,
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
    }));
  } else {
    window.localStorage.removeItem(SESSION_KEY);
  }
  currentSession = session;
}

// Initialize session on client side
if (typeof window !== 'undefined') {
  const storedSession = loadSession();
  if (storedSession) {
    currentSession = {
      token: '',
      user: {
        id: storedSession.userId,
        email: storedSession.email,
        role: storedSession.role
      }
    };
  }
}

export const login = async (email: string, password: string) => {
  const session = await apiLogin(email, password);
  if (session && session.requires2FA) {
    return session;
  }
  saveSession(session);
  return session;
};

export const finalize2FASession = (session: any) => {
  saveSession(session);
};

export const validateSession = async () => {
  const storedSession = getCurrentSession();
  if (!storedSession) {
    if (currentSession) {
      // Clear the in-memory session if localStorage session is gone
      currentSession = null;
    }
    return null;
  }

  try {
    const validatedUser = await apiValidateSession();
    if (!validatedUser) {
      saveSession(null);
      return null;
    }

    const validatedSession = {
      token: '',
      user: validatedUser
    };
    saveSession(validatedSession);
    return validatedSession;
  } catch (error) {
    saveSession(null);
    return null;
  }
};

export const getCurrentSession = (): UserSession | null => {
  try {
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;

    const session = JSON.parse(sessionData);
    if (session.expiresAt < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      currentSession = null;
      return null;
    }

    return session;
  } catch (error) {
    localStorage.removeItem(SESSION_KEY);
    currentSession = null;
    return null;
  }
};

export const setCurrentSession = (session: UserSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify({
    userId: session.userId,
    email: session.email,
    role: session.role,
    expiresAt: session.expiresAt
  }));
  currentSession = {
    token: '',
    user: {
      id: session.userId,
      email: session.email,
      role: session.role
    }
  };
};

export const clearCurrentSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
  currentSession = null;
};

export const getUserPreferences = (): UserPreferences => {
  try {
    const prefsData = localStorage.getItem(PREFERENCES_KEY);
    if (!prefsData) {
      return {
        theme: 'light',
        notifications: true,
        language: 'en'
      };
    }
    return JSON.parse(prefsData);
  } catch (error) {
    return {
      theme: 'light',
      notifications: true,
      language: 'en'
    };
  }
};

export const setUserPreferences = (prefs: UserPreferences): void => {
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
};

export const logout = () => {
  fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  }).catch(() => {
    // Ignore logout network failures and clear local state anyway.
  });
  saveSession(null);
};

// Helper function to generate UUIDs
export const generateUUID = () => {
  return crypto.randomUUID();
};
/* eslint-enable no-undef */
